import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../services/providers/authProvider";
import { AccessGateContext } from "../../../../context/accessGateContext";
import accountService from "../../../../services/accountService";
import { dispatchAppError } from "../../../../services/errors/errorDispatcher";
import { validateFileByMimeAndSignature } from "../../../../services/fileValidation";
import { featureFlags } from "../../../../config/featureConfig";
import pagamenti from "../../../../services/pagamenti";
import { LOGO_MAX_BYTES, formFields, sortPaymentMethodIds } from "../constants";
import {
  addressSnapshotsEqual,
  hasAnyAddressValue,
  pickAddressSnapshot,
  sedeAddressToVendorPatch,
  sedeFieldKeyToVendorKey,
  vendorFormToSedeAddress,
} from "../addressFormMapping";
import { mapProfileFromApi } from "../utils/mapProfileFromApi";
import { buildProfilePatch } from "../utils/profilePatchBuilder";
import { validateVendorForm } from "../utils/profileValidation";

function applyBaseDataToForm(prev, baseData) {
  const next = { ...prev };
  for (const { name } of formFields) {
    next[name] = baseData[name] ?? "";
  }
  return next;
}

export function useVendorProfile() {
  const navigate = useNavigate();
  const { canAccessVendorArea } = useContext(AccessGateContext);
  const { token, vendorId, isImpersonated } = useAuth() || {};

  const [profileData, setProfileData] = useState(null);
  const [isInitialProfileLoading, setIsInitialProfileLoading] = useState(true);
  const [baseData, setBaseData] = useState({});
  const [vendorForm, setVendorForm] = useState({});
  const [successFeedback, setSuccessFeedback] = useState({ open: false, title: "", message: "" });
  const [tempFile, setTempFile] = useState(null);
  const [base64, setBase64] = useState(null);
  const fileInputRef = useRef(null);
  const [metodiPagamenti, setMetodiPagamenti] = useState([]);
  const [paymentSelectedIds, setPaymentSelectedIds] = useState([]);
  const [paymentCommittedIds, setPaymentCommittedIds] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [addressVerifiedSnapshot, setAddressVerifiedSnapshot] = useState({});
  const [geocodeErrorOpen, setGeocodeErrorOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const resolvedVendorId = useMemo(() => {
    if (vendorId == null || vendorId === "") return null;
    const n = Number(vendorId);
    return Number.isFinite(n) ? n : null;
  }, [vendorId]);

  const canLoadPaymentMethods = featureFlags.enableMetodiPagamento && resolvedVendorId != null;

  const hasStoredAddress = useMemo(() => hasAnyAddressValue(baseData), [baseData]);
  const addressNeedsReverify = useMemo(
    () => !addressSnapshotsEqual(vendorForm, addressVerifiedSnapshot),
    [vendorForm, addressVerifiedSnapshot]
  );
  const addressBlocksSave = useMemo(() => {
    if (!featureFlags.enableSedeAziendaleValidationService) return false;
    if (!addressNeedsReverify) return false;
    return hasAnyAddressValue(vendorForm);
  }, [addressNeedsReverify, vendorForm]);

  const hasFormFieldChanges = useMemo(() => {
    for (const field of formFields) {
      if ((vendorForm[field.name] ?? "") !== (baseData[field.name] ?? "")) {
        return true;
      }
    }
    return false;
  }, [vendorForm, baseData]);

  const hasLogoChanges = useMemo(() => (baseData.logo ?? "") !== (base64 ?? ""), [baseData, base64]);
  const hasProfileDirty = hasFormFieldChanges || hasLogoChanges;

  const hasPaymentDirty = useMemo(() => {
    if (!featureFlags.enableMetodiPagamento) return false;
    const a = sortPaymentMethodIds(paymentSelectedIds);
    const b = sortPaymentMethodIds(paymentCommittedIds);
    if (a.length !== b.length) return true;
    return a.some((id, i) => id !== b[i]);
  }, [paymentSelectedIds, paymentCommittedIds]);

  const isUnifiedDirty = hasProfileDirty || hasPaymentDirty;
  const { profileSchemaValid, fieldErrors } = useMemo(
    () => validateVendorForm(vendorForm),
    [vendorForm]
  );

  const applyProfileToState = useCallback((profile) => {
    const initial = mapProfileFromApi(profile, vendorId);
    if (!initial) return;
    setBaseData(initial);
    setVendorForm(initial);
    setAddressVerifiedSnapshot(pickAddressSnapshot(initial));
    setBase64(profile.logo && profile.logo.length > 0 ? profile.logo : null);
    setTempFile(null);
  }, [vendorId]);

  const refreshProfile = useCallback(async () => {
    try {
      const response = await accountService.getVendorProfile();
      setProfileData(response?.data ?? null);
    } catch (error) {
      console.error("Errore durante il recupero profilo vendor:", error);
    }
  }, []);

  useEffect(() => {
    if (!token) {
      setIsInitialProfileLoading(false);
      return;
    }
    let cancelled = false;
    setIsInitialProfileLoading(true);
    (async () => {
      try {
        const response = await accountService.getVendorProfile();
        if (!cancelled) setProfileData(response?.data ?? null);
      } catch (error) {
        console.error("Errore durante il recupero profilo vendor:", error);
        if (!cancelled) setProfileData(null);
      } finally {
        if (!cancelled) setIsInitialProfileLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!canAccessVendorArea) {
      navigate("/");
      return;
    }
    if (!profileData) return;
    applyProfileToState(profileData);
  }, [canAccessVendorArea, navigate, profileData, applyProfileToState]);

  useEffect(() => {
    if (!featureFlags.enableMetodiPagamento || !token || !canLoadPaymentMethods) {
      setMetodiPagamenti([]);
      setPaymentCommittedIds([]);
      setPaymentSelectedIds([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await pagamenti.metodiPagamenti(resolvedVendorId, token);
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setMetodiPagamenti(list);
        const associated = list.filter((d) => d.associato === true).map((d) => d.id);
        const next = sortPaymentMethodIds(associated);
        setPaymentCommittedIds(next);
        setPaymentSelectedIds([...next]);
      } catch (err) {
        if (!cancelled) {
          console.error("Errore durante il recupero dei metodi di pagamento:", err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canLoadPaymentMethods, resolvedVendorId, token]);

  useEffect(() => {
    if (!tempFile) return;
    const reader = new FileReader();
    reader.readAsDataURL(tempFile);
    reader.onloadend = () => setBase64(reader.result);
    reader.onerror = () => dispatchAppError("Errore nella lettura del file.");
    return () => reader.abort();
  }, [tempFile]);

  const handleChange = useCallback((event) => {
    const { name, value } = event.target;
    setVendorForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSedeFieldChange = useCallback((sedeKey, val) => {
    const vk = sedeFieldKeyToVendorKey(sedeKey);
    if (!vk) return;
    setVendorForm((prev) => ({ ...prev, [vk]: val }));
  }, []);

  const handleSedeVerifiedMerge = useCallback((normalizedSedeForm) => {
    const patch = sedeAddressToVendorPatch(normalizedSedeForm);
    setVendorForm((prev) => {
      const next = { ...prev, ...patch };
      setAddressVerifiedSnapshot(pickAddressSnapshot(next));
      return next;
    });
  }, []);

  const handleSedeResetToVerified = useCallback(() => {
    setVendorForm((prev) => ({ ...prev, ...pickAddressSnapshot(addressVerifiedSnapshot) }));
  }, [addressVerifiedSnapshot]);

  const handleReset = useCallback(() => {
    setVendorForm((prev) => applyBaseDataToForm(prev, baseData));
    setAddressVerifiedSnapshot(pickAddressSnapshot(baseData));
    setTempFile(null);
    setBase64(baseData.logo && baseData.logo.length > 0 ? baseData.logo : null);
    setPaymentSelectedIds(sortPaymentMethodIds(paymentCommittedIds));
  }, [baseData, paymentCommittedIds]);

  const togglePaymentMethodId = useCallback(
    (id) => {
      if (isImpersonated || !isEditing) return;
      setPaymentSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    },
    [isEditing, isImpersonated]
  );

  const handleStartEdit = useCallback(() => {
    if (isImpersonated) return;
    setIsEditing(true);
  }, [isImpersonated]);

  const handleCancelEdit = useCallback(() => {
    handleReset();
    setIsEditing(false);
  }, [handleReset]);

  const handleRemoveLogo = useCallback(() => {
    setTempFile(null);
    setBase64(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const persistProfilePatch = useCallback(async (patchData) => {
    try {
      const response = await accountService.modificaAccount(patchData);
      if (response?.data?.statusCode === 202) {
        return { ok: true };
      }
      return {
        ok: false,
        message: response?.data?.message ?? "Si è verificato un errore nella richiesta.",
      };
    } catch (error) {
      return {
        ok: false,
        message: error?.response?.data?.message ?? "Si è verificato un errore nella richiesta.",
      };
    }
  }, []);

  const persistPaymentMethods = useCallback(async () => {
    if (resolvedVendorId == null || !token) {
      return { ok: false, message: "Sessione non valida per il salvataggio dei metodi di pagamento." };
    }
    try {
      const result = metodiPagamenti.map((metodo) => ({
        idMetodoPagamento: metodo.id,
        associa: paymentSelectedIds.includes(metodo.id),
      }));
      await pagamenti.associaMetodiPagamentoUtente(resolvedVendorId, result, token);
      return { ok: true };
    } catch (error) {
      return {
        ok: false,
        message: error?.response?.data?.message ?? "Errore durante l'aggiornamento dei metodi di pagamento.",
      };
    }
  }, [metodiPagamenti, paymentSelectedIds, resolvedVendorId, token]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (isImpersonated || !isEditing) return;
      if (!isUnifiedDirty) return;
      if (addressBlocksSave) return;
      if (hasFormFieldChanges && !profileSchemaValid) return;

      const { patchData, hasPatch } = buildProfilePatch({ vendorForm, baseData, base64 });
      const runProfile = hasProfileDirty && hasPatch;
      const runPayment = hasPaymentDirty && featureFlags.enableMetodiPagamento && resolvedVendorId != null;

      if (!runProfile && !runPayment) return;

      setSaveLoading(true);
      try {
        if (runProfile) {
          const pr = await persistProfilePatch(patchData);
          if (!pr.ok) return;
        }
        if (runPayment) {
          const pay = await persistPaymentMethods();
          if (!pay.ok) return;
        }

        setIsEditing(false);

        if (runProfile && runPayment) {
          setSuccessFeedback({
            open: true,
            title: "Profilo aggiornato",
            message: "Dati aziendali e metodi di pagamento sono stati salvati correttamente.",
          });
        } else if (runProfile) {
          setSuccessFeedback({
            open: true,
            title: "Account modificato con successo",
            message: "Le modifiche sono state salvate correttamente.",
          });
        } else {
          setSuccessFeedback({
            open: true,
            title: "Operazione completata",
            message: "Le modifiche ai metodi di pagamento sono state salvate.",
          });
        }
      } finally {
        setSaveLoading(false);
      }
    },
    [
      addressBlocksSave,
      base64,
      baseData,
      hasFormFieldChanges,
      hasPaymentDirty,
      hasProfileDirty,
      isEditing,
      isImpersonated,
      isUnifiedDirty,
      persistPaymentMethods,
      persistProfilePatch,
      profileSchemaValid,
      resolvedVendorId,
      vendorForm,
    ]
  );

  const handleFileChange = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = null;
    const validTypes = ["image/png", "image/jpeg"];
    if (!validTypes.includes(file.type)) {
      setTempFile(null);
      dispatchAppError("Formato non supportato. Carica un file PNG o JPEG.");
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      setTempFile(null);
      dispatchAppError("File troppo grande. Il logo non deve superare 1MB.");
      return;
    }
    const mimeValidation = await validateFileByMimeAndSignature(file, validTypes);
    if (!mimeValidation.ok) {
      setTempFile(null);
      dispatchAppError(
        "Contenuto file non valido. Assicurati che il file sia realmente un PNG o JPEG e non solo rinominato."
      );
      return;
    }
    setTempFile(file);
  }, []);

  const handleSuccessDialogOk = useCallback(async () => {
    setSuccessFeedback((s) => ({ ...s, open: false }));
    await refreshProfile();
    setPaymentCommittedIds(sortPaymentMethodIds(paymentSelectedIds));
  }, [refreshProfile, paymentSelectedIds]);

  const showLogoPreview = Boolean(base64);
  const companyName = (vendorForm.nomeCommerciale ?? "").trim() || "La tua azienda";
  const showInitialProfileSpinner = Boolean(token) && isInitialProfileLoading;

  const submitDisabled =
    !isUnifiedDirty ||
    isImpersonated ||
    addressBlocksSave ||
    (hasFormFieldChanges && !profileSchemaValid);

  return {
    fileInputRef,
    token,
    isImpersonated,
    isEditing,
    vendorForm,
    fieldErrors,
    base64,
    showLogoPreview,
    companyName,
    showInitialProfileSpinner,
    metodiPagamenti,
    paymentSelectedIds,
    saveLoading,
    successFeedback,
    geocodeErrorOpen,
    setGeocodeErrorOpen,
    hasStoredAddress,
    addressNeedsReverify,
    submitDisabled,
    handleChange,
    handleSubmit,
    handleStartEdit,
    handleCancelEdit,
    handleFileChange,
    handleRemoveLogo,
    handleSedeFieldChange,
    handleSedeVerifiedMerge,
    handleSedeResetToVerified,
    togglePaymentMethodId,
    handleSuccessDialogOk,
    sedeAddressValues: vendorFormToSedeAddress(vendorForm),
  };
}
